import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator,
} from '@mui/icons-material';
import { Responsive as ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import {
  LIVE_STUDIO_WIDGET_IDS,
  createDefaultLiveStudioLayout,
} from './liveStudioSettings';

export const LIVE_STUDIO_GRID_BREAKPOINTS = { lg: 900, xs: 0 };
export const LIVE_STUDIO_GRID_COLS = { lg: 12, xs: 1 };
export const LIVE_STUDIO_GRID_ROW_HEIGHT = 56;

const WIDGET_LABELS = {
  participants: 'Participant video',
  mixer: 'Server mix',
  playout: 'Playout log',
  side: 'Cartwall & meters',
};

export default function LiveStudioWidgets({
  layouts,
  visibleWidgetIds = LIVE_STUDIO_WIDGET_IDS,
  editing = false,
  onLayoutChange,
  onRemoveWidget,
  renderWidget,
}) {
  const { width: gridWidth, containerRef, mounted } = useContainerWidth();
  const defaults = createDefaultLiveStudioLayout();
  const safeLayouts = {
    lg: Array.isArray(layouts?.lg) ? layouts.lg : defaults.lg,
    xs: Array.isArray(layouts?.xs) ? layouts.xs : defaults.xs,
  };
  const visible = (visibleWidgetIds || LIVE_STUDIO_WIDGET_IDS).filter((id) =>
    LIVE_STUDIO_WIDGET_IDS.includes(id)
  );

  return (
    <Box
      ref={containerRef}
      className={editing ? 'live-studio-grid live-studio-grid-editing' : 'live-studio-grid'}
      sx={{ flex: 1, minHeight: 0, width: '100%' }}
    >
      {mounted && (
        <ResponsiveGridLayout
          width={gridWidth}
          layouts={safeLayouts}
          breakpoints={LIVE_STUDIO_GRID_BREAKPOINTS}
          cols={LIVE_STUDIO_GRID_COLS}
          rowHeight={LIVE_STUDIO_GRID_ROW_HEIGHT}
          margin={[12, 12]}
          containerPadding={[0, 0]}
          dragConfig={{
            enabled: editing,
            handle: '.live-studio-widget-drag-handle',
            cancel: '.live-studio-no-drag',
          }}
          resizeConfig={{ enabled: editing, handles: ['se', 'e', 's'] }}
          onLayoutChange={onLayoutChange}
        >
          {visible.map((widgetId) => (
            <div key={widgetId}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  overflow: 'hidden',
                  cursor: editing ? 'grab' : 'default',
                }}
              >
                {renderWidget(widgetId)}
                {editing && (
                  <Box
                    className="live-studio-widget-drag-handle"
                    title={`Drag to move ${WIDGET_LABELS[widgetId]}`}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      left: 6,
                      right: widgetId === 'side' ? 44 : 6,
                      zIndex: 4,
                      height: 30,
                      px: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      borderRadius: 1.5,
                      bgcolor: 'var(--rs-overlay-strong)',
                      border: '1px solid var(--rs-live-console-border-active)',
                      color: 'var(--rs-accent)',
                      cursor: 'grab',
                      userSelect: 'none',
                      '&:active': { cursor: 'grabbing' },
                    }}
                  >
                    <DragIndicator fontSize="small" />
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                      Move {WIDGET_LABELS[widgetId]}
                    </Typography>
                  </Box>
                )}
                {editing && widgetId === 'side' && (
                  <IconButton
                    className="live-studio-no-drag"
                    size="small"
                    aria-label={`Hide ${WIDGET_LABELS[widgetId]}`}
                    onClick={() => onRemoveWidget?.(widgetId)}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      zIndex: 5,
                      bgcolor: 'var(--rs-overlay-strong)',
                      border: '1px solid rgba(148,163,184,0.35)',
                      color: 'text.secondary',
                      '&:hover': { bgcolor: 'rgba(239,68,68,0.25)', color: '#fca5a5' },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </Box>
  );
}

export const liveStudioWidgetLabel = (id) => WIDGET_LABELS[id] || id;
